import { useEffect, useState } from 'react';
import { RepeatIcon } from 'lucide-react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Button,
  DataTable,
  DatePicker,
  EmptyState,
  ErrorNotice,
  PageHeading,
  Pagination,
  Panel,
  Pill,
  SearchInput,
  MultiCombobox,
  SelectInput,
  SkeletonTable,
  Tabs,
  useFromHere,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { formatMoney } from '@/lib/cash-flow';
import { useTableSort } from '@/lib/use-table-sort';
import { useUrlPage, useUrlSearch } from '@/lib/use-list-query-state';
import { formatDay, salesOpportunityPath } from '@/lib/sales-workspace';
import {
  ALL_REPS,
  DEFAULT_OUTCOMES,
  OUTCOME_ANY,
  dealOutcomeLabel,
  QUEUE_SORT,
  QUEUE_STATUS,
  REPEAT_CLIENTS_PATH,
  readRepeatClients,
  repeatClientPath,
  repeatClientsRepPath,
  setRepeatClientStatus,
  type QueueRow,
  type QueueSort,
  type QueueStatus,
  type RepeatClientsPage as PageData,
} from '@/lib/repeat-clients';
import { SnoozeDialog } from '@/components/repeat-clients/SnoozeDialog';
import { OUTREACH_PLAYBOOK_PATH } from '@/lib/outreach-playbook';
import { OWNER_PARAM, GMAIL_QUERY_KEY, GMAIL_RESULT, GMAIL_RESULT_PARAM, gmailErrorMessage, opensLabel, outreachEmailPath } from '@/lib/outreach-email';

export const REPEAT_CLIENTS_QUERY_KEY = ['repeat-clients'] as const;

/** The statuses a rep works through, as tabs (Booked clients drop off the list). */
const STATUS_TABS = [QUEUE_STATUS.toContact, QUEUE_STATUS.contacted, QUEUE_STATUS.snoozed, QUEUE_STATUS.notInterested] as const;
type StatusTab = (typeof STATUS_TABS)[number];
export const STATUS_LABEL: Readonly<Record<StatusTab, string>> = {
  [QUEUE_STATUS.toContact]: 'To contact',
  [QUEUE_STATUS.contacted]: 'Contacted',
  [QUEUE_STATUS.snoozed]: 'Snoozed',
  [QUEUE_STATUS.notInterested]: 'Not interested',
};
const STATUS_EMPTY: Readonly<Record<StatusTab, string>> = {
  [QUEUE_STATUS.toContact]: 'Nobody is due by this date. Pick a later date to see who comes up next.',
  [QUEUE_STATUS.contacted]: 'Clients you mark contacted show here for six months.',
  [QUEUE_STATUS.snoozed]: 'Snoozed clients wait here until their date, then come back to To contact.',
  [QUEUE_STATUS.notInterested]: 'Clients marked not interested stay here until you move them back.',
};

const STATUS_PARAM = 'status';
/** Repeated: `?outcome=booked&outcome=lost:Limited%20Budget`; absent is Booked, `any` is every outcome. */
const OUTCOME_PARAM = 'outcome';
const BY_PARAM = 'by';

const SORT_KEYS = Object.values(QUEUE_SORT);
/** Soonest suggested reach-out first: who to call next. */
const DEFAULT_SORT = { key: QUEUE_SORT.suggested, direction: 'asc' } as const;

function isStatusTab(value: string | null): value is StatusTab {
  return STATUS_TABS.some((tab) => tab === value);
}
function isSort(value: string): value is QueueSort {
  return SORT_KEYS.some((key) => key === value);
}

/** `yyyy-mm-dd` plus whole days. */
function addDays(day: string, days: number): string {
  const [year, month, date] = day.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(year, month - 1, date + days)).toISOString().slice(0, 10);
}

export interface RepeatClientsQueueProps {
  /** Whose clients (a Salesforce user id); undefined is the viewer's own. */
  readonly repId: string | undefined;
  /** Drawn above the status tabs once the queue has loaded (the page's heading and rep picker). */
  readonly header?: (data: PageData | undefined) => React.ReactNode;
}

/**
 * The Repeat clients queue: a rep's past clients due a call, one page at a
 * time. Each client is To contact, Contacted, Snoozed or Not interested;
 * the status lives in one record per account, so nobody gets two emails. Used by
 * the Repeat clients page.
 *
 * @returns The status tabs, filters, table and pager.
 */
export function RepeatClientsQueue({ repId, header }: RepeatClientsQueueProps): React.ReactElement {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // Back from a client returns to this exact view (status, filters, page).
  const fromHere = useFromHere();
  const [params, setParams] = useSearchParams();
  const { search, setSearch, debounced } = useUrlSearch();
  const { page, setPage } = useUrlPage();
  const { sort, onSort } = useTableSort(SORT_KEYS, DEFAULT_SORT);
  const [snoozing, setSnoozing] = useState<QueueRow | null>(null);

  const statusParam = params.get(STATUS_PARAM);
  const status: StatusTab = isStatusTab(statusParam) ? statusParam : QUEUE_STATUS.toContact;
  const outcomeParams = params.getAll(OUTCOME_PARAM);
  // undefined: the default (Booked); []: any outcome.
  const outcomes: readonly string[] | undefined =
    outcomeParams.length === 0 ? undefined : outcomeParams.includes(OUTCOME_ANY) ? [] : outcomeParams;
  const setOutcomes = (next: readonly string[]): void =>
    setParams((previous) => {
      const updated = new URLSearchParams(previous);
      updated.delete(OUTCOME_PARAM);
      updated.delete('page');
      const isDefault = next.length === DEFAULT_OUTCOMES.length && next.every((key) => DEFAULT_OUTCOMES.includes(key));
      if (next.length === 0) updated.append(OUTCOME_PARAM, OUTCOME_ANY);
      else if (!isDefault) for (const key of next) updated.append(OUTCOME_PARAM, key);
      return updated;
    });
  const reachOutBy = params.get(BY_PARAM) ?? undefined;

  const setParam = (key: string, value: string | null): void =>
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      if (value === null) next.delete(key);
      else next.set(key, value);
      next.delete('page');
      return next;
    });

  const queue = useQuery({
    queryKey: [...REPEAT_CLIENTS_QUERY_KEY, repId ?? 'me', status, outcomes?.join('|') ?? 'default', reachOutBy ?? '', debounced, sort?.key, sort?.direction, page],
    queryFn: ({ signal }) =>
      readRepeatClients(
        {
          ...(repId ? { ownerId: repId } : {}),
          status,
          ...(outcomes !== undefined ? { outcomes } : {}),
          ...(reachOutBy ? { reachOutBy } : {}),
          q: debounced,
          ...(sort && isSort(sort.key) ? { sort: sort.key, dir: sort.direction } : {}),
          page,
        },
        signal,
      ),
    placeholderData: keepPreviousData,
  });

  const data = queue.data;
  const showOwner = data !== undefined && !data.viewingSelf;

  const move = useMutation({
    mutationFn: (input: { row: QueueRow; status: QueueStatus; snoozeUntil?: string }) =>
      setRepeatClientStatus(input.row.accountId, {
        status: input.status,
        // Someone else's client (a manager's view): say whose, so the server can check it.
        ...(data && !data.viewingSelf ? { ownerId: input.row.ownerId } : {}),
        ...(input.snoozeUntil ? { snoozeUntil: input.snoozeUntil } : {}),
      }),
    onSuccess: (_result, input) => {
      setSnoozing(null);
      toast.success(`${input.row.accountName || 'Client'} moved to ${STATUS_LABEL[input.status as StatusTab] ?? 'Booked'}`);
      void queryClient.invalidateQueries({ queryKey: REPEAT_CLIENTS_QUERY_KEY });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const clientPath = (row: QueueRow): string => {
    const path = repeatClientPath(row.accountId);
    return data && !data.viewingSelf ? `${path}?${new URLSearchParams({ [OWNER_PARAM]: row.ownerId }).toString()}` : path;
  };
  const emailPath = (row: QueueRow): string => outreachEmailPath(row.accountId, data && !data.viewingSelf ? row.ownerId : undefined);

  const actions = (row: QueueRow): React.ReactElement => {
    const busy = move.isPending && move.variables?.row.accountId === row.accountId;
    if (status !== QUEUE_STATUS.toContact) {
      return (
        <span className="inline-flex gap-2">
          <Button size="compact" variant="secondary" onClick={() => navigate(emailPath(row))}>
            Email
          </Button>
          <Button size="compact" variant="secondary" disabled={busy} onClick={() => move.mutate({ row, status: QUEUE_STATUS.toContact })}>
            Back to To contact
          </Button>
        </span>
      );
    }
    return (
      <span className="inline-flex gap-2">
        <Button size="compact" onClick={() => navigate(emailPath(row))}>
          Email
        </Button>
        <Button size="compact" variant="secondary" disabled={busy} onClick={() => move.mutate({ row, status: QUEUE_STATUS.contacted })}>
          Contacted
        </Button>
        <Button size="compact" variant="secondary" disabled={busy} onClick={() => setSnoozing(row)}>
          Snooze
        </Button>
        <Button size="compact" variant="secondary" disabled={busy} onClick={() => move.mutate({ row, status: QUEUE_STATUS.notInterested })}>
          Not interested
        </Button>
      </span>
    );
  };

  return (
    <>
      {header?.(data)}
      <div className="mb-4">
        <Tabs
          label="Which clients to show"
          active={status}
          onChange={(next) => setParam(STATUS_PARAM, next === QUEUE_STATUS.toContact ? null : next)}
          items={STATUS_TABS.map((key) => ({ key, label: STATUS_LABEL[key], ...(data ? { count: data.counts[key] } : {}) }))}
        />
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <SearchInput
          label="Search repeat clients"
          placeholder="Account, program or opportunity"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="w-full max-w-sm"
        />
        {status === QUEUE_STATUS.toContact ? (
          <label className="flex items-center gap-2 text-portal-small text-portal-ink">
            Reach out by
            <DatePicker
              aria-label="Reach out by"
              value={data?.reachOutBy ?? reachOutBy ?? ''}
              onChange={(next) => setParam(BY_PARAM, next || null)}
              className="w-48 [&>button]:mt-0"
            />
          </label>
        ) : null}
        <div className="ml-auto flex min-w-80 max-w-xl items-center gap-2 text-portal-small text-portal-ink">
          <label htmlFor="repeat-outcomes" className="whitespace-nowrap">
            Outcome
          </label>
          <div className="min-w-0 flex-1">
            <MultiCombobox
              id="repeat-outcomes"
              placeholder="Any outcome"
              values={outcomes ?? DEFAULT_OUTCOMES}
              onChange={setOutcomes}
              options={(data?.outcomeOptions ?? []).map((option) => ({ value: option.key, label: `${option.label} (${option.clients})` }))}
            />
          </div>
        </div>
      </div>

      {queue.isError ? (
        <ErrorNotice title="Repeat clients unavailable" message={errorMessage(queue.error)} />
      ) : queue.isPending ? (
        <SkeletonTable label="Loading repeat clients…" />
      ) : data === undefined || data.owner === null ? (
        <Panel>
          <EmptyState
            icon={<RepeatIcon aria-hidden className="size-6" />}
            title="Not linked to Salesforce yet"
            message="Your account is not linked to a Salesforce user, so there are no clients to show. Ask an admin to link it."
          />
        </Panel>
      ) : data.rows.length === 0 ? (
        <Panel>
          {debounced ? (
            <EmptyState message="No repeat clients match that search." />
          ) : (
            <EmptyState icon={<RepeatIcon aria-hidden className="size-6" />} title={`${STATUS_LABEL[status]}: nobody here`} message={STATUS_EMPTY[status]} />
          )}
        </Panel>
      ) : (
        <>
          <DataTable
            headers={[
              { label: status === QUEUE_STATUS.snoozed ? 'Snoozed until' : 'Reach out', sortKey: QUEUE_SORT.suggested },
              { label: 'Account', sortKey: QUEUE_SORT.account },
              ...(showOwner ? ['Salesperson'] : []),
              'Reached out before',
              'Last email',
              { label: 'Event anniversary', sortKey: QUEUE_SORT.nextEvent },
              { label: 'Bookings', sortKey: QUEUE_SORT.bookings },
              { label: 'Leads', sortKey: QUEUE_SORT.leads },
              { label: 'Booked value', sortKey: QUEUE_SORT.value },
              'Actions',
            ]}
            rightAlignedHeaders={showOwner ? [6, 7, 8] : [5, 6, 7]}
            visuallyHiddenHeaders={[showOwner ? 9 : 8]}
            sort={sort}
            onSort={onSort}
            minWidth={1320}
          >
            {data.rows.map((row) => {
              const overdue = row.suggestedReachOutDate < data.today;
              // Newest first: only the latest reach-out shows, with how many came before.
              const [latest] = row.deals;
              const earlier = row.deals.length - 1;
              return (
                <tr
                  key={row.accountId}
                  className="cursor-pointer border-t border-portal-line align-middle hover:bg-portal-bg"
                  onClick={(event) => {
                    // Links and buttons in the row do their own thing.
                    if ((event.target as HTMLElement).closest('a, button')) return;
                    navigate(clientPath(row), { state: fromHere });
                  }}
                >
                  <td className="w-[130px] whitespace-nowrap p-3 text-portal-small">
                    {status === QUEUE_STATUS.snoozed && row.snoozedUntil ? (
                      <b className="block text-portal-small">{formatDay(row.snoozedUntil, { withYear: true })}</b>
                    ) : (
                      <>
                        <b className={`block text-portal-small ${overdue ? 'text-rose-700' : ''}`}>{formatDay(row.suggestedReachOutDate, { withYear: true })}</b>
                        {overdue ? <span className="block text-portal-micro text-rose-700">Overdue</span> : null}
                      </>
                    )}
                  </td>
                  <td className="w-[18%] p-3 text-portal-small">
                    <Link to={clientPath(row)} state={fromHere} className="block text-portal-small font-bold text-portal-ink hover:text-brand hover:underline">
                      {row.accountName || 'Unnamed account'}
                    </Link>
                    <span className="block text-portal-micro text-portal-muted">{row.programName || 'No program recorded'}</span>
                  </td>
                  {showOwner ? <td className="whitespace-nowrap p-3 text-portal-small text-portal-muted">{row.ownerName || '—'}</td> : null}
                  <td className="whitespace-nowrap p-3 text-portal-small">
                    {latest ? (
                      <>
                        <span className="flex items-center gap-2">
                          <Link to={salesOpportunityPath(latest.id)} className="font-bold text-brand hover:underline">
                            {formatDay(latest.reachedOutDate, { withYear: true })}
                          </Link>
                          <Pill tone={latest.booked ? 'success' : 'neutral'}>{dealOutcomeLabel(latest)}</Pill>
                        </span>
                        <span className="block text-portal-micro text-portal-muted">
                          {latest.eventDate ? `Event ${formatDay(latest.eventDate, { withYear: true })}` : 'No event date'}
                          {earlier > 0 ? ` · ${earlier} earlier` : ''}
                        </span>
                      </>
                    ) : (
                      <span className="text-portal-muted">—</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap p-3 text-portal-small">
                    {row.lastEmail ? (
                      <>
                        <span className="block text-portal-small">{formatDay(row.lastEmail.sentAt.slice(0, 10), { withYear: true })}</span>
                        <span className={`block text-portal-micro ${row.lastEmail.opens > 0 ? 'font-bold text-emerald-700' : 'text-portal-muted'}`}>
                          {opensLabel(row.lastEmail.opens)}
                        </span>
                      </>
                    ) : (
                      <span className="text-portal-muted">—</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap p-3 text-portal-small">{row.nextEventDate ? formatDay(row.nextEventDate, { withYear: true }) : '—'}</td>
                  <td className="whitespace-nowrap p-3 text-right text-portal-small">{row.bookings}</td>
                  <td className="whitespace-nowrap p-3 text-right text-portal-small text-portal-muted">{row.leads}</td>
                  <td className="whitespace-nowrap p-3 text-right text-portal-small">{row.bookedValue > 0 ? formatMoney(row.bookedValue) : '—'}</td>
                  <td className="whitespace-nowrap p-3 text-right text-portal-small">{actions(row)}</td>
                </tr>
              );
            })}
          </DataTable>
          {data.total > data.pageSize ? (
            <Pagination
              page={data.page}
              totalPages={Math.ceil(data.total / data.pageSize)}
              onChange={setPage}
              summary={`${(data.page - 1) * data.pageSize + 1}–${(data.page - 1) * data.pageSize + data.rows.length} of ${data.total}`}
            />
          ) : null}
        </>
      )}

      {snoozing && data ? (
        <SnoozeDialog
          accountName={snoozing.accountName}
          min={addDays(data.today, 1)}
          saving={move.isPending}
          onClose={() => setSnoozing(null)}
          onSnooze={(until) => move.mutate({ row: snoozing, status: QUEUE_STATUS.snoozed, snoozeUntil: until })}
        />
      ) : null}
    </>
  );
}

/**
 * Sales → Repeat clients, on its own grant (work_repeat_clients). Managers
 * (view_sales_dashboard) can open any rep's queue at /repeat-clients/rep/:repId.
 *
 * @returns The page.
 */
export function RepeatClientsPage(): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { repId } = useParams();
  const [params, setParams] = useSearchParams();

  // Back from Google after connecting Gmail: say how it went, then drop the flag from the URL.
  const gmailResult = params.get(GMAIL_RESULT_PARAM);
  useEffect(() => {
    if (gmailResult === null) return;
    if (gmailResult === GMAIL_RESULT.connected) toast.success('Gmail connected. Your emails now send from your mailbox.');
    else toast.error(gmailErrorMessage(params.get('reason')));
    void queryClient.invalidateQueries({ queryKey: GMAIL_QUERY_KEY });
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete(GMAIL_RESULT_PARAM);
        next.delete('reason');
        return next;
      },
      { replace: true },
    );
  }, [gmailResult, params, queryClient, setParams]);
  return (
    <section className="animate-portal-rise">
      <RepeatClientsQueue
        repId={repId}
        header={(data) => {
          const viewingSelf = data?.viewingSelf ?? repId === undefined;
          return (
            <>
              <PageHeading
                eyebrow="SALES"
                title={viewingSelf ? 'Repeat clients' : data?.allReps ? 'All repeat clients' : `${data?.owner?.ownerName || 'This rep'}’s repeat clients`}
                subtitle="Past clients due a call: when they asked before, what they booked, and when to reach out again."
                action={
                  <Link to={OUTREACH_PLAYBOOK_PATH} className="text-portal-small font-bold text-brand hover:underline">
                    My reach-out playbook
                  </Link>
                }
              />
              {data && data.reps.length > 0 ? (
                <label className="mb-4 flex items-center gap-2 text-portal-small text-portal-ink">
                  Salesperson
                  <SelectInput
                    density="compact"
                    aria-label="Whose clients to show"
                    value={repId ?? ''}
                    onChange={(event) => {
                      const next = new URLSearchParams(params);
                      next.delete('page');
                      const query = next.toString();
                      const path = event.target.value ? repeatClientsRepPath(event.target.value) : REPEAT_CLIENTS_PATH;
                      navigate(query ? `${path}?${query}` : path);
                    }}
                    className="w-64"
                  >
                    <option value="">My clients</option>
                    <option value={ALL_REPS}>All salespeople</option>
                    {data.reps.map((rep) => (
                      <option key={rep.ownerId} value={rep.ownerId}>
                        {rep.ownerName || rep.ownerId}
                      </option>
                    ))}
                  </SelectInput>
                </label>
              ) : null}
            </>
          );
        }}
      />
    </section>
  );
}
